import { SelectItem } from '@wui/common-library'
import {
  AnyConferenceChangedByOtherEvent,
  CallPastMessage,
  ConferenceCreatedByOtherEvent,
  GroupStatusEvent,
  HistoryMessageItem,
  MessageOutgoingEvent,
  ReactionAddedByOtherEvent,
  ReactionAddedByYouEvent,
  useIM,
  MessageIncomingEvent,
  MessageCallReceived,
  PServed,
  ContactState,
  ConferenceType,
  BriefStatusResponse,
  HistoryResponse,
  ConferenceMemberAddedByOtherEvent,
  NotificationIncomingEvent,
  ConferenceRemovedByOtherEvent } from '@wui/im'
import { defineStore, storeToRefs } from 'pinia'
import { v4 as uuid } from 'uuid'
import { computed, ref, watch } from 'vue'

import router from '@/app/router'
import { useNotificationPreferenceStore } from '@/app/store/notifications/preferences'
import { useStoreWaitingApprove } from '@/app/store/pinia/waiting-approve/waiting-approve-store'
import { useGuestAuth } from '@/composables/useGuestAuth'

import { Guest } from '@/entities/conference-participant'
import { useStoreContacts } from '@/entities/contact'
import { chatModel } from '@/entities/messages/chat'
import { draftModel } from '@/entities/messages/draft'

import { useChatCleanupHook } from '@/shared/composables/chat-handlers'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useStatusSubscribe } from '@/shared/composables/im'
import { isGuest } from '@/shared/composables/local-storage'
import { useLocalization } from '@/shared/composables/localization'
import { useNotification } from '@/shared/composables/use-notification'
import { emitter } from '@/shared/services/emitter'
import { useWebRTC } from '@/shared/services/jssip'
import { Contact } from '@/shared/types/api'
import { RoomListItem } from '@/shared/types/app'
import { EmitterEvents } from '@/shared/types/emitter-events'
import { AddOrRemoveRoomMembers, RemoveRoomMember, Room, UpdateRoomMemberPayload } from '@/shared/types/store'
import { pathNames, PSERVED_ROOM_PREFIX, PSTNGUEST, GUEST } from '@/shared/utils/constants'
import { convertNumbersToSubscribersMapWithCurrentSubscriber, createPrivateRoom } from '@/shared/utils/im'
import { fromPServedUser, fromPServedUserIfRoom, pServedIsRoom, toPServedUser, isPServedIsUser, isPstnGuest, getNumberFromPstn } from '@/shared/utils/uri'

export const useStoreRooms = defineStore('rooms', () => {
  const {
    onConferenceUpdated,
    onConferenceCreated,
    onMessage,
    onConferenceEdited,
    requestBriefStatus,
    onHistory,
    onNotification,
  } = useIM()
  const { t } = useLocalization()
  const { currentUser } = useCurrentUser()

  const rooms = ref(new Map<string, Room>())

  const roomsList = computed(() => {
    const { findLastMessage } = chatModel.useChatMessageStore()
    const array: RoomListItem[] = []
    for (const room of rooms.value.values()) {
      const draft = draftModel.useDraftMessageStore().findByPServed(room.pServedUser)?.orElse(undefined)
      array.push({
        ...room,
        name: room.pServedUser === currentUser.value?.imLogin ? t('common.savedMessages') : room.name,
        roomId: room.id,
        lastMessage: findLastMessage(room.pServedUser) as HistoryMessageItem<ReactionAddedByOtherEvent | ReactionAddedByYouEvent | MessageIncomingEvent | MessageOutgoingEvent | CallPastMessage>,
        avatar: room.avatar!,
        draft: draft!,
      })
    }
    return array.sort((a: RoomListItem, b: RoomListItem) => {
      if (a.pServedUser === currentUser.value?.imLogin) {
        return - 1
      }

      if (b.pServedUser === currentUser.value?.imLogin) {
        return 1
      }

      if (a.isPinned) {
        return -1
      }

      if (b.isPinned) {
        return 1
      }

      // TODO При каждом клике на элемент в списке чатов, происходит сортировка
      const lastMessageTimeA = a.lastMessage?.timestamp ?? 0
      const draftTimeA = a.draft?.timestamp ?? 0
      const lastMessageTimeB = b.lastMessage?.timestamp ?? 0
      const draftTimeB = b.draft?.timestamp ?? 0

      const timestampA = (lastMessageTimeA < draftTimeA) ? draftTimeA : lastMessageTimeA
      const timestampB = (lastMessageTimeB < draftTimeB) ? draftTimeB : lastMessageTimeB

      return timestampB - timestampA
    })
  })

  const roomsWithOtherContactsListItems = computed<RoomListItem[]>(() => {
    const list: RoomListItem[] = [...roomsList.value]

    allContacts.value.forEach((contact) => {
      if (rooms.value.has(contact.imLogin)) {
        return
      }

      list.push({
        roomId: contact.imLogin,
        id: contact.imLogin,
        pServedUser: contact.imLogin,
        isGroup: false,
        name: contact?.name,
        number: contact.internalNumber,
        subscribers: new Map([[contact.imLogin, contact]]),
        isExternal: false,
        isEnabled: true,
      })
    })

    return list
  })

  const getGuestRoom = computed(() => rooms.value.values().next().value)

  const contactsItemsSortedByUsing = computed<SelectItem<Contact>[]>(() => {
    const { allContacts } = storeToRefs(useStoreContacts())
    const { findByPServed } = useStoreContacts()

    const { currentUser } = useCurrentUser()

    const contacts = roomsList.value
      .reduce((acc, room) => {
        if (!room.isGroup && room.pServedUser !== currentUser.value?.imLogin) {
          const contact = findByPServed(room.pServedUser)
          if (contact) {
            acc.push({
              label: contact.name,
              value: contact,
            })
          }
        }
        return acc
      }, [] as SelectItem<Contact>[])
    // Остальные контакты, с которыми нет чатов
    allContacts.value.forEach(contact => {
      if (!contacts.find(i => i.value.id === contact.id)) {
        contacts.push({
          label: contact.name,
          value: contact,
        })
      }
    })

    return contacts
  })

  const freeAndWhereIAmModeratorRooms = computed(() => [...rooms.value.values()].filter(room => {
    const currentUserContact = room.contacts?.find(c => c.id === currentUser.value?.imLogin)
    return room.mode === ConferenceType.FREE_FOR_ALL || (room.mode === ConferenceType.MODERATED && (currentUserContact?.role === 'Moderator' || currentUserContact?.role === 'Owner'))
  }))

  const roomsWithIncompleteSubscribersList = computed(() => {
    return [...rooms.value.values()].filter(room => room.contacts?.length ?? 0 > (room.subscribers.size + (room.guests?.size ?? 0)))
  })

  const notFetchedContactsIdsForRooms = computed(() => {
    const result = new Map<string, string[]>()
    roomsWithIncompleteSubscribersList.value.forEach(r => {
      r.contacts?.forEach(c => {
        if (!r.subscribers.has(c.id)) {
          if (!result.has(c.id)) {
            result.set(c.id, [])
          }

          const arr = [...(result.get(c.id) as string[]), ...[r.pServedUser]]
          result.set(c.id, arr)
        }
      })
    })
    return result
  })

  /**
   * @deprecated Use findByPServed method
   * @param roomId
   * @returns
   */
  const getRoomById = (roomId: string) => rooms.value.get(roomId)

  const findByPServed = (pServed: Room['pServedUser']): Room | undefined => {
    return Array.from(rooms.value, ([, value]) => value).find(({ pServedUser }) => pServed === pServedUser)
  }
  const findGuestByPserved = (roomPServed: PServed, guestPServed: PServed) => {
    return findByPServed(roomPServed)?.guests?.get(guestPServed)?.name
  }

  const createRoom = (payload: Room) => {
    rooms.value.set(payload.id, payload)
  }

  const updateRoom = (roomId: string, payload: Partial<Room>) => {
    const room = rooms.value.get(roomId)
    if (room) {
      Object.assign(room, payload)
    }
  }
  const setRoomGuest = (roomId: string, guest: Guest) => {
    const room = rooms.value.get(roomId)
    if (!room) return
    room.guests?.set(guest.pServed, guest)
  }
  const removeGuest = (roomId: string, pServed: string) => {
    const room = getRoomById(roomId)
    if (!room) return
    if (room.guests?.has(pServed)) {
      room.guests?.delete(pServed)
    }
  }
  const setRoomMember = (roomId: string, number: string) => {
    const room = rooms.value.get(roomId)
    if (!room) return
    const storeContacts = useStoreContacts()
    const contact = storeContacts.findByInternalNumber(number)
    if (contact) {
      room.subscribers.set(uuid(), contact) // TODO Убрать uuid
    } else {
      if (isPstnGuest(number)) {
        const numberWithoutPstnGuest = getNumberFromPstn(number)
        if (room.subscribers.has(numberWithoutPstnGuest)) {
          return
        }
      }

      const externalContact = storeContacts.createExternalContact(number)
      room.subscribers.set(uuid(), externalContact)
    }
  }
  const addRoomMember = (payload: UpdateRoomMemberPayload) => {
    const room = rooms.value.get(payload.roomId)
    if (!room || room.subscribers.has(payload.roomSubscriberGuid)) return

    const { currentUser } = useCurrentUser()
    const storeContacts = useStoreContacts()
    const { list } = storeToRefs(storeContacts)
    if (currentUser.value?.internalNumber === payload.number) {
      room.subscribers.set(payload.roomSubscriberGuid, currentUser.value as Contact)
    } else {
      const contact = list.value.find((x) => x.internalNumber === payload.number)
      if (contact) {
        room.subscribers.set(payload.roomSubscriberGuid, contact)
      } else {
        const externalContact = storeContacts.createExternalContact(payload.number)
        room.subscribers.set(payload.roomSubscriberGuid, externalContact)
      }
    }
  }
  const removeRoomMember = (payload: RemoveRoomMember) => {
    const room = rooms.value.get(payload.roomId)
    if (!room) return
    if (room.subscribers.has(payload.roomSubscriberGuid)) {
      room.subscribers.delete(payload.roomSubscriberGuid)
    }
  }
  const removeRoom = (roomId: string) => {
    if (rooms.value.has(roomId)) {
      rooms.value.delete(roomId)
    }
  }
  const removeRoomMembers = (payload: AddOrRemoveRoomMembers) => {
    const room = rooms.value.get(payload.roomId)
    if (!room) return

    for (const [key, { internalNumber }] of room.subscribers) {
      if (payload.members?.includes(internalNumber)) {
        removeRoomMember({
          roomId: payload.roomId,
          roomSubscriberGuid: key,
        })
      }
    }
  }
  const awaitForRoomAdded = (number: string, timeout = 5000): Promise<Room> => {
    return new Promise((resolve, reject) => {
      let timer = 0
      const timerId = setInterval(() => {
        timer += 100
        const room = rooms.value.get(number) ?? rooms.value.get(toPServedUser(number, true))
        if (room) {
          resolve(room)
          clearInterval(timerId)
        } else if (timer > timeout) {
          reject(new Error(`Can't await for Room with id ${number} after timeout ${timeout}`))
          clearInterval(timerId)
        }
      }, 100)
    })
  }

  const createEmptyGroupRoom = (pServed: string) => {
    const guid = fromPServedUser(pServed)
    if (rooms.value.has(guid)) return

    const room: Room = {
      id: guid,
      pServedUser: pServed,
      name: guid,
      subscribers: new Map(),
      isGroup: true,
      number: guid,
    }
    createRoom(room)
    requestBriefStatus({ contactId: pServed }).then(({ name }: BriefStatusResponse) => {
      updateRoom(guid, {
        name,
      })
    })
  }
  const updateFromContactStatus = (status: GroupStatusEvent): void => {
    const { pServed, name, extendedParams: {
      avatar,
      token,
      contacts: c = [],
      mode,
      pin,
      favorite,
      isEnabled,
      isAutoRecording,
      isRecording,
      isActive,
      pinEnabled,
    },
    } = status
    const { findByPServed, createExternalContact, findContactByMobilePhone } = useStoreContacts()
    const { currentUser } = useCurrentUser()
    const { setWaitingApprovePSTNGuests } = useStoreWaitingApprove()
    const { currentGuest } = useGuestAuth()

    const isRoom = pServedIsRoom(pServed)
    const id = isRoom ? fromPServedUser(pServed) : pServed
    const room = rooms.value.get(id)
    if (!isRoom && room) {
      room.isPinned = favorite!
    }

    if (currentUser.value?.imLogin === pServed) {
      return
    }

    const contactStates = c as ContactState[]

    if (isRoom && (contactStates.findIndex(({ id }) => id === currentUser.value?.imLogin || id === currentGuest.value?.pServed) < 0)) {
      return
    }

    if (!isRoom) {
      if (!room) {
        createPrivateRoom(pServed, {
          imLogin: pServed,
          internalNumber: fromPServedUser(pServed),
          id: pServed,
          groupIds: [],
          name,
        })
      }
      return
    }

    if (room && room.isExternal) {
      // due to receive contact object, room is not external anymore
      room.isExternal = false
    }

    const waitingApprovePstnGuests = new Map<string, Contact>() // Для state 4 WaitingApprove
    const subscribers = new Map<string, Contact>()
    const guests = new Map<string, Guest>()

    let notFoundMembers: ContactState[] = []
    let notFoundGuests: ContactState[] = []

    for (const contact of contactStates) {
      if (contact.type === 'pstnGuest') {
        // здесь contact.name у добавленного внешнего это мобильный номер
        const mobilePhone = contact.id
        const externalContact = findContactByMobilePhone(mobilePhone)
        const alreadyHasContact = room?.subscribers.has(mobilePhone)

        if (externalContact) {
          subscribers.set(mobilePhone, externalContact)
        } else if (!alreadyHasContact) {
          notFoundGuests.push(contact)
        } else {
          subscribers.set(mobilePhone, createExternalContact(mobilePhone, contact.name))
        }
        if (contact.state === 4) {
          const pstnGuest = createExternalContact(fromPServedUser(mobilePhone), contact.name, 'pstnGuest')
          waitingApprovePstnGuests.set(mobilePhone, pstnGuest)
        }
      } else if (contact.type === 'member') {
        const pServed = contact.id
        const subscriber = currentUser.value?.imLogin === pServed ? currentUser.value : findByPServed(pServed)
        if (subscriber) {
          subscribers.set(pServed, subscriber)
        } else {
          notFoundMembers.push(contact)
        }
      } else if (contact.type === 'guest') {
        const guest: Guest = {
          pServed: contact.id,
          name: contact.name,
          state: contact.state,
          type: 'guest',
        }
        guests.set(contact.id, guest)
      }
    }

    setWaitingApprovePSTNGuests(pServed, waitingApprovePstnGuests) // Для state 4 WaitingApprove

    if (currentGuest.value) {
      useStoreContacts().addContactsForGuestRoom(status)
    }

    if (room) {
      room.avatar = avatar!
      room.token = token!
      room.name = name
        ?? room.name // TODO remove after fixing the backend
      room.subscribers = subscribers
      room.guests = guests
      room.isRecording = Boolean(isRecording)
      room.contacts = [...contactStates]
      room.mode = mode!
      room.pin = pin!
      room.pinEnabled = Boolean(pinEnabled)
      room.isActive = Boolean(isActive)
      room.isPinned = favorite!
      room.isEnabled = isEnabled!
      room.isAutoRecording = isAutoRecording!
    } else {
      const room = {
        id,
        pServedUser: pServed,
        name,
        avatar: avatar!,
        token: token!,
        subscribers,
        guests,
        isGroup: true,
        number: id,
        contacts: [...contactStates],
        mode: mode!,
        pin: pin!,
        isEnabled: isEnabled!,
        isAutoRecording: isAutoRecording!,
        isPinned: favorite!,
      }
      createRoom(room)
    }

    useWebRTC().linkRoomToCallSessions(room as Room)

    if (notFoundGuests.length) {
      useStoreContacts().fetchContactsBySomeIds(notFoundGuests.map((c) => c.id), 'mobilePhone')
        .then((contacts: Contact[] | undefined) => {
          contacts?.forEach((c) => {
            room?.subscribers.set(c.mobilePhone ?? c.internalNumber, c)
            notFoundGuests = notFoundGuests.filter((notFoundContact) => notFoundContact.id !== c.id)
          })
        })
      notFoundGuests.forEach((contact) => {
        const externalContact = createExternalContact(contact.id, contact.name)
        room?.subscribers.set(contact.id, externalContact)
      })
      notFoundGuests.length = 0
    }

    if (notFoundMembers.length) {
      useStoreContacts().fetchContactsBySomeIds(notFoundMembers.map((c) => c.id))
        .then((contacts: Contact[] | undefined) => {
          contacts?.forEach((c) => {
            if (!room?.subscribers.has(c.imLogin) && c.imLogin) {
              room?.subscribers.set(c.imLogin, c)
            }
            notFoundMembers = notFoundMembers.filter((notFoundContact) => notFoundContact.id !== c.id)
          })
        })

      notFoundMembers.forEach((contact) => {
        const externalContact = createExternalContact(contact.id, contact.name)
        room?.subscribers.set(contact.id, externalContact)
      })
      notFoundMembers.length = 0
    }
  }
  const isOutsideRoom = (room: Room | RoomListItem): boolean => {
    const subscribersCount = room.subscribers.size
    if (room.isGroup || room.isExternal) return false
    if (subscribersCount > 1) return false

    const contact = room.subscribers.values().next().value
    return !contact?.imLogin // Для внешнего контакта imLogin = ""
  }
  const createGuestRoom = (payload: GroupStatusEvent): Room => {
    const contacts: ContactState[] = []
    payload.extendedParams?.contacts?.forEach((c) => {
      const state = c as ContactState
      if (state.type !== 'guest') {
        contacts.push(state)
      }
    })
    const subscribers = contacts.reduce((acc, contact: ContactState) => {
      acc.set(contact.id, {
        id: contact.id,
        name: contact.name,
        internalNumber: contact.id,
        organization: '',
        position: '',
        imLogin: contact.id,
        terminalLogin: '',
        terminalPassword: '',
        groupIds: [],
      })
      return acc
    }, new Map<string, Contact>())
    const id = fromPServedUser(payload.pServed)
    return {
      id,
      pServedUser: payload.pServed,
      name: payload.name,
      avatar: '',
      isGroup: true,
      number: id,
      subscribers,
      isRecording: Boolean(payload.extendedParams?.isRecording),
    }
  }

  const $reset = () => {
    rooms.value = new Map()
  }

  onConferenceUpdated((event: GroupStatusEvent) => {
    if (isGuest() && event.pServed.includes(PSERVED_ROOM_PREFIX)) {
      const nextRoom = createGuestRoom(event)
      const existingRoom = rooms.value.get(nextRoom.id)
      if (existingRoom) {
        existingRoom.name = nextRoom.name
        existingRoom.subscribers = nextRoom.subscribers
        existingRoom.isRecording = Boolean(nextRoom.isRecording)
        useWebRTC().updateRoomInSessionIfNeed(existingRoom)
      } else {
        createRoom(nextRoom)
        useWebRTC().updateRoomInSessionIfNeed(nextRoom)
      }
      useStoreContacts().addContactsForGuestRoom(event)
    }
    if (event.extendedParams.isEnabled || isPServedIsUser(event.pServed)) {
      updateFromContactStatus(event)
    }
    useStoreContacts().updateStatus(event)
    const disabled = event.extendedParams?.disabledNotification ?? false
    useNotificationPreferenceStore().addRoomsNotificationAccess(event.pServed, disabled)
  })

  onConferenceCreated((event: ConferenceCreatedByOtherEvent) => {
    const { pServed, name, members, token } = event
    const id = fromPServedUser(pServed)
    createRoom({
      id,
      pServedUser: pServed,
      name,
      isGroup: true,
      subscribers: convertNumbersToSubscribersMapWithCurrentSubscriber(members ?? []),
      number: id,
      guests: new Map<string, Guest>(),
      token: token!,
    })
    useNotification().createYouAreAddedToRoomNotification(event)
  })

  onNotification((event: NotificationIncomingEvent) => {
    const { type, sourceRemoteId, eventId, sender, rank, timestamp } = event
    if (type === 'ConferenceRemoved') {
      const room = findByPServed(sourceRemoteId)
      if (room) {
        const e: ConferenceRemovedByOtherEvent = {
          eventId,
          executor: sender,
          pServed: sourceRemoteId,
          rank,
          timestamp,
          type: 'ConferenceRemoved',
        }
        useChatCleanupHook(room, e)
      }
    }
  })

  onConferenceEdited((event: AnyConferenceChangedByOtherEvent) => {
    const { pServed } = event
    const { currentGuest, logout } = useGuestAuth()
    const roomId = fromPServedUserIfRoom(pServed)
    const room = findByPServed(pServed)
    if (event.type === 'ConferenceMemberAddedEvent') {
      const { targetNumber } = event
      const targetId = fromPServedUser(pServed)
      const isCurrentUser = targetNumber === useCurrentUser().currentUser.value?.internalNumber
      if (isCurrentUser) {
        createEmptyGroupRoom(pServed)
        useStatusSubscribe().subscribe(pServed)
      }
      if (targetNumber) {
        setRoomMember(targetId, targetNumber)
      }
    } else if (event.type === 'ConferenceMemberRemoved') {
      const targetNumber = event.targetNumber ?? fromPServedUser(event.executor)
      if (currentGuest.value && targetNumber === currentGuest.value.pServed) {
        router.push({ name: pathNames.BYE }).then(() => logout())
      } else if (room) {
        const isGuest = targetNumber && targetNumber.includes(GUEST) && !targetNumber.includes(PSTNGUEST)
        if (isGuest) {
          removeGuest(room.id, targetNumber)
        } else {
          const removeRoomMembersPayload = {
            roomId: room.id,
            members: [targetNumber],
          }
          const isCurrentUserDeleted = targetNumber === useCurrentUser().currentUser.value?.internalNumber
          if (isCurrentUserDeleted) {
            useChatCleanupHook(room, event)
          } else {
            removeRoomMembers(removeRoomMembersPayload)
          }
        }
      }
    } else if (event.type === 'ConferenceRemoved') {
      if (currentGuest.value) {
        router.push({ name: pathNames.BYE }).then(() => logout())
      } else if (room) {
        useChatCleanupHook(room, event)
      }
    } else if (event.type === 'ConferenceRenamedEvent') {
      const { newName } = event
      updateRoom(roomId, {
        name: newName,
      })
    } else if (event.type === 'ConferenceAvatarChangedEvent') {
      const { avatar } = event
      updateRoom(roomId, {
        avatar: avatar!,
      })
    } else if (event.type === 'ConferenceGuestAccessEnabled') {
      const { token } = event
      updateRoom(roomId, {
        token: token!,
      })
    } else if (event.type === 'ConferenceGuestAccessDisabled') {
      updateRoom(roomId, { token: '' })
    }
    emitter.emit(EmitterEvents.NEW_MESSAGE, { event })
    if (event.type === 'ConferenceMemberRemoved' && event.executor === currentUser.value?.imLogin) {
      return
    }
    useNotification().createAnyConferenceChangedByOtherEventNotification(event)
  })

  onMessage((event: MessageIncomingEvent | MessageCallReceived) => {
    const { pServed } = event
    const roomId = fromPServedUserIfRoom(pServed)
    if (!rooms.value.has(roomId)) {
      const contact = useStoreContacts().findByPServed(roomId)
      if (pServed !== currentUser.value?.imLogin) {
        if (contact) {
          createPrivateRoom(roomId, contact) // TODO replace roomId to pServed
        }
      }
    }
  })

  onHistory((history: HistoryResponse) => {
    if (history.events.length === 1) {
      const item = history.events.at(0)
      const typedItem = item?.item as unknown as ConferenceMemberAddedByOtherEvent
      const { pServed, type, targetNumber } = typedItem
      if (
        !typedItem ||
        type !== 'ConferenceMemberAddedEvent' ||
        targetNumber !== useCurrentUser().currentUser.value?.internalNumber
      ) {
        return
      }

      createEmptyGroupRoom(pServed)
      useStatusSubscribe().subscribe(pServed)
    }
  })

  const ensureSavedMessagesRoom = () => {
    if (!currentUser.value || rooms.value.has(currentUser.value.imLogin)) {
      return
    }

    createRoom({
      id: currentUser.value.imLogin,
      pServedUser: currentUser.value.imLogin,
      avatar: '',
      isGroup: false,
      name: t('common.savedMessages'),
      number: currentUser.value.internalNumber,
      subscribers: new Map(),
      isExternal: false,
    })
  }
  watch(
    () => currentUser.value, v => {
      if (v) {
        ensureSavedMessagesRoom()
      }
    },
    { immediate: true },
  )

  const { allContacts } = storeToRefs(useStoreContacts())
  watch(() => allContacts?.value?.length, (n, o) => {
    if (n > o && notFetchedContactsIdsForRooms.value.size) {
      allContacts.value.forEach(c => {
        if (notFetchedContactsIdsForRooms.value.has(c.imLogin)) {
          const pServeds = notFetchedContactsIdsForRooms.value.get(c.imLogin)
          pServeds?.forEach(pServed => {
            const room = findByPServed(pServed)
            room?.subscribers.set(c.imLogin, c)
          })
        }
      })
    }
  })

  return {
    rooms,
    roomsList,
    getGuestRoom,
    contactsItemsSortedByUsing,
    freeAndWhereIAmModeratorRooms,
    roomsWithOtherContactsListItems,
    getRoomById,
    findByPServed,
    findGuestByPserved,
    createRoom,
    updateRoom,
    setRoomGuest,
    removeGuest,
    setRoomMember,
    addRoomMember,
    removeRoomMember,
    removeRoom,
    removeRoomMembers,
    awaitForRoomAdded,
    createEmptyGroupRoom,
    updateFromContactStatus,
    isOutsideRoom,
    $reset,
  }
})
